import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const EMPTY = {
  first_name: "", last_name: "", company: "", address: "", email: "", phone: "",
  opportunity_id: "", investor_id: "", confidentiality_duration: "24 mois",
  jurisdiction: "Tribunaux de Paris, France", signature_recipient: "", signature_admin: "",
};

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-slate-700">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export default function NdaForm({ open, onOpenChange, initial, presetOpportunityId, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [opps, setOpps] = useState([]);
  const [investors, setInvestors] = useState([]);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/opportunities"), api.get("/investors")]).then(([o, i]) => {
      setOpps(o.data);
      setInvestors(i.data);
    });
    setForm(initial ? { ...EMPTY, ...initial } : { ...EMPTY, opportunity_id: presetOpportunityId || "" });
  }, [open, initial, presetOpportunityId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickInvestor = (id) => {
    const inv = investors.find((x) => x.id === id);
    if (!inv) return;
    const parts = (inv.contact || "").trim().split(" ");
    setForm((f) => ({
      ...f,
      investor_id: id,
      company: inv.name || f.company,
      first_name: parts[0] || f.first_name,
      last_name: parts.slice(1).join(" ") || f.last_name,
    }));
  };

  const submit = async () => {
    if (!form.first_name || !form.last_name) return toast.error("Nom et prénom obligatoires");
    if (!form.opportunity_id) return toast.error("Sélectionnez une opportunité");
    setSaving(true);
    const payload = { ...form, investor_id: form.investor_id || null };
    try {
      if (initial?.id) {
        await api.put(`/ndas/${initial.id}`, payload);
        toast.success("NDA modifié");
      } else {
        await api.post("/ndas", payload);
        toast.success("NDA créé");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          onClick={() => onOpenChange(false)}
          data-testid="nda-form-backdrop"
        />
      )}
      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogContent
          className="max-h-[90vh] max-w-2xl overflow-y-auto"
          data-testid="nda-form"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0F172A]">
            {initial?.id ? "Modifier le NDA" : "Envoyer un NDA"}
          </DialogTitle>
          <DialogDescription className="sr-only">Formulaire de création d'un accord de confidentialité.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Opportunité concernée">
            <Select value={form.opportunity_id} onValueChange={(v) => set("opportunity_id", v)} disabled={!!presetOpportunityId && !initial}>
              <SelectTrigger className="h-10" data-testid="nda-opportunity"><SelectValue placeholder="Choisir une opportunité" /></SelectTrigger>
              <SelectContent>
                {opps.map((o) => <SelectItem key={o.id} value={o.id} data-testid={`nda-opp-${o.id}`}>{o.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Pré-remplir depuis un investisseur">
            <Select value={form.investor_id || ""} onValueChange={pickInvestor}>
              <SelectTrigger className="h-10" data-testid="nda-investor"><SelectValue placeholder="Optionnel" /></SelectTrigger>
              <SelectContent>
                {investors.map((i) => <SelectItem key={i.id} value={i.id} data-testid={`nda-inv-${i.id}`}>{i.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Prénom"><Input data-testid="nda-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} /></Field>
          <Field label="Nom"><Input data-testid="nda-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} /></Field>
          <Field label="Société"><Input data-testid="nda-company" value={form.company} onChange={(e) => set("company", e.target.value)} /></Field>
          <Field label="Email"><Input data-testid="nda-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="Téléphone"><Input data-testid="nda-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
          <Field label="Adresse"><Input data-testid="nda-address" value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="Durée de confidentialité"><Input data-testid="nda-duration" value={form.confidentiality_duration} onChange={(e) => set("confidentiality_duration", e.target.value)} /></Field>
          <Field label="Juridiction applicable"><Input data-testid="nda-jurisdiction" value={form.jurisdiction} onChange={(e) => set("jurisdiction", e.target.value)} /></Field>
          <Field label="Signature du destinataire"><Input data-testid="nda-sig-recipient" value={form.signature_recipient} onChange={(e) => set("signature_recipient", e.target.value)} placeholder="Nom manuscrit / initiales" /></Field>
          <Field label="Signature de l'administrateur"><Input data-testid="nda-sig-admin" value={form.signature_admin} onChange={(e) => set("signature_admin", e.target.value)} placeholder="Nom manuscrit / initiales" /></Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="nda-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="nda-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
